import { useEffect } from 'react';
import { DEFAULT_LANG, I18N, type Dict, type Lang } from '@/i18n';
import { SITE_NAME, SITE_TAGLINE, absoluteUrl, pathWithLang } from '@/lib/site';
import { findArticle, listArticles } from '@/lib/blog';
import { findTool, listTools } from '@/lib/tools';
import { allFaqs } from '@/lib/faq';
import { NEVERZERO_PRICE_AMOUNT, NEVERZERO_PRICE_CURRENCY } from '@/lib/pricing';
import { FOUNDERS, type Founder } from '@/content/social-proof';

/** Pages légales servies par `LegalPage` : chemin → titre. */
const LEGAL_PATHS: Record<string, (t: Dict) => string> = {
  '/privacy': (t) => t.privacy,
  '/terms': (t) => t.terms,
  '/extensions/privacy': (t) => t.extensionsPrivacy,
  '/extensions/terms': (t) => t.extensionsTerms,
};

export interface Alternate {
  hrefLang: string;
  href: string;
}

export interface HeadData {
  lang: Lang;
  title: string;
  description: string;
  canonicalPath: string;
  alternates: Alternate[];
  ogType: 'website' | 'article';
  /** Chemin (`/…`) ou URL https de l'image de partage ; rendue en URL absolue. */
  image?: string;
  /** Le serveur SSR renvoie ce code : une route inconnue doit répondre 404, pas 200. */
  status: 200 | 404;
  robots?: string;
  jsonLd: unknown[];
}

/**
 * Les deux versions linguistiques d'une même page se déclarent mutuellement.
 * Sans ça, Google voit deux pages quasi identiques et en choisit une au hasard.
 */
function bilingualAlternates(path: string): Alternate[] {
  return [
    { hrefLang: 'en', href: pathWithLang(path, 'en') },
    { hrefLang: 'fr', href: pathWithLang(path, 'fr') },
    { hrefLang: 'x-default', href: pathWithLang(path, DEFAULT_LANG) },
  ];
}

/**
 * Qui signe un article quand son en-tête ne nomme pas un fondateur. Décision
 * de Stephen (08/10/2026) : les articles sont signés par une personne, pas par
 * l'agence — les moteurs IA pèsent un auteur nommé, pas une marque. Un
 * `author:` qui nomme un fondateur garde la main.
 */
const DEFAULT_ARTICLE_AUTHOR = 'Stephen Zambaux';

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/** Les `@id` relient l'agence, ses fondateurs et ses articles en un seul graphe. */
function organizationId(origin: string): string {
  return `${origin}/#organization`;
}

function founderId(founder: Founder, origin: string): string {
  return `${origin}/#${slugify(founder.name)}`;
}

/**
 * `sameAs` = le profil LinkedIn déclaré dans `FOUNDERS` : c'est ce lien qui
 * relie l'auteur à son profil pour les moteurs.
 * `url` pointe la section équipe de la home (`#team`), la seule page du site
 * qui présente les fondateurs — toujours la home par défaut, pour qu'une même
 * entité (`@id`) n'ait qu'une seule `url` quelle que soit la langue de la page.
 */
function founderJsonLd(founder: Founder, lang: Lang, origin: string) {
  return {
    '@type': 'Person',
    ...(origin ? { '@id': founderId(founder, origin) } : {}),
    name: founder.name,
    jobTitle: founder.role[lang],
    sameAs: [founder.linkedin],
    ...(origin
      ? {
          url: `${absoluteUrl('/', origin)}#team`,
          image: absoluteUrl(founder.photo.fallback, origin),
          worksFor: { '@id': organizationId(origin) },
        }
      : {}),
  };
}

function articleAuthor(name: string): Founder {
  return (
    FOUNDERS.find((founder) => founder.name === name) ??
    FOUNDERS.find((founder) => founder.name === DEFAULT_ARTICLE_AUTHOR) ??
    FOUNDERS[0]
  );
}

/** L'éditeur d'un article : l'agence, renvoyée par `@id` quand l'origine est connue. */
function publisherJsonLd(origin: string) {
  return {
    '@type': 'Organization',
    ...(origin ? { '@id': organizationId(origin), url: `${origin}/` } : {}),
    name: SITE_NAME,
    // PNG 512×512 rendu depuis favicon.svg : Google ne prend pas de SVG comme
    // logo d'organisation (raster, 112 px minimum).
    ...(origin
      ? { logo: { '@type': 'ImageObject', url: absoluteUrl('/logo.png', origin), width: 512, height: 512 } }
      : {}),
  };
}

function organizationJsonLd(lang: Lang, origin: string) {
  return {
    '@context': 'https://schema.org',
    ...publisherJsonLd(origin),
    description: SITE_TAGLINE,
    founder: FOUNDERS.map((founder) => founderJsonLd(founder, lang, origin)),
  };
}

export function headFor(pathname: string, lang: Lang, origin: string): HeadData {
  const t = I18N[lang];
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/') {
    return {
      lang,
      title: `${SITE_NAME} | ${t.seoTitle}`,
      description: t.heroP1,
      canonicalPath: pathWithLang('/', lang),
      alternates: bilingualAlternates('/'),
      ogType: 'website',
      status: 200,
      jsonLd: [
        organizationJsonLd(lang, origin),
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: SITE_NAME,
          serviceType: 'LinkedIn post engagement',
          description: t.heroP1,
          ...(origin ? { provider: { '@id': organizationId(origin) } } : {}),
          offers: {
            '@type': 'Offer',
            price: String(NEVERZERO_PRICE_AMOUNT),
            priceCurrency: NEVERZERO_PRICE_CURRENCY.toUpperCase(),
            // `valueAddedTaxIncluded: false` colle à `tax_behavior: 'exclusive'`
            // côté Stripe. Si le prix passe TTC, les deux changent ensemble.
            valueAddedTaxIncluded: false,
            availability: 'https://schema.org/InStock',
          },
        },
      ],
    };
  }

  if (path === '/blog') {
    return {
      lang,
      title: `${t.blogTitle} | ${SITE_NAME}`,
      description: t.blogIntro,
      canonicalPath: pathWithLang('/blog', lang),
      alternates: bilingualAlternates('/blog'),
      ogType: 'website',
      status: 200,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: t.blogTitle,
          description: t.blogIntro,
          ...(origin ? { url: absoluteUrl(pathWithLang('/blog', lang), origin) } : {}),
          blogPost: listArticles()
            .slice(0, 20)
            .map((article) => ({
              '@type': 'BlogPosting',
              headline: article.title,
              datePublished: article.date,
              ...(origin ? { url: absoluteUrl(pathWithLang(`/blog/${article.slug}`, article.lang), origin) } : {}),
            })),
        },
      ],
    };
  }

  const blogMatch = /^\/blog\/([^/]+)$/.exec(path);
  if (blogMatch) {
    const article = findArticle(decodeURIComponent(blogMatch[1]));
    if (article) {
      // Un article n'existe que dans SA langue : pas d'alternate vers une
      // traduction qui n'a pas été écrite.
      return {
        lang: article.lang,
        title: `${article.title} | ${SITE_NAME}`,
        description: article.description,
        canonicalPath: pathWithLang(`/blog/${article.slug}`, article.lang),
        alternates: [],
        ogType: 'article',
        image: article.cover,
        status: 200,
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: article.title,
            description: article.description,
            datePublished: article.date,
            dateModified: article.date,
            inLanguage: article.lang,
            author: founderJsonLd(articleAuthor(article.author), article.lang, origin),
            publisher: publisherJsonLd(origin),
            ...(article.cover && (origin || article.cover.startsWith('https://'))
              ? { image: absoluteImageUrl(article.cover, origin) }
              : {}),
            ...(origin
              ? { mainEntityOfPage: absoluteUrl(pathWithLang(`/blog/${article.slug}`, article.lang), origin) }
              : {}),
          },
        ],
      };
    }
  }

  if (path === '/tools') {
    return {
      lang,
      title: `${t.toolsPageTitle} | ${SITE_NAME}`,
      description: t.toolsPageIntro,
      canonicalPath: pathWithLang('/tools', lang),
      alternates: bilingualAlternates('/tools'),
      ogType: 'website',
      status: 200,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: t.toolsPageTitle,
          description: t.toolsPageIntro,
          ...(origin ? { url: absoluteUrl(pathWithLang('/tools', lang), origin) } : {}),
          hasPart: listTools(lang).map((tool) => ({
            '@type': 'SoftwareApplication',
            name: tool.title,
            ...(origin ? { url: absoluteUrl(pathWithLang(`/tools/${tool.slug}`, tool.lang), origin) } : {}),
          })),
        },
      ],
    };
  }

  const toolMatch = /^\/tools\/([^/]+)$/.exec(path);
  if (toolMatch) {
    const tool = findTool(decodeURIComponent(toolMatch[1]));
    if (tool) {
      // Comme un article de blog, un outil n'existe que dans SA langue : pas
      // d'alternate vers une traduction qui n'a pas été construite.
      return {
        lang: tool.lang,
        title: `${tool.title} | ${SITE_NAME}`,
        description: tool.metaDescription,
        canonicalPath: pathWithLang(`/tools/${tool.slug}`, tool.lang),
        alternates: [],
        ogType: 'website',
        status: 200,
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: tool.title,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: tool.metaDescription,
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
            ...(origin
              ? { url: absoluteUrl(pathWithLang(`/tools/${tool.slug}`, tool.lang), origin) }
              : {}),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: tool.faq.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          },
        ],
      };
    }
  }

  if (path === '/faq') {
    return {
      lang,
      title: `${t.faqPageTitle} | ${SITE_NAME}`,
      description: t.faqPageIntro,
      canonicalPath: pathWithLang('/faq', lang),
      alternates: bilingualAlternates('/faq'),
      ogType: 'website',
      status: 200,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: allFaqs(t).map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: { '@type': 'Answer', text: faq.a },
          })),
        },
      ],
    };
  }

  if (path === '/welcome') {
    return {
      lang,
      title: `${t.welcomeTitle} | ${SITE_NAME}`,
      description: t.welcomeLead,
      // Pas d'alternates et pas de canonical vers une autre langue : cette page
      // n'est pas une destination, c'est le retour d'un paiement. Son URL porte
      // un `session_id`, qu'on ne veut ni indexé ni canonique.
      canonicalPath: pathWithLang(path, lang),
      alternates: [],
      ogType: 'website',
      status: 200,
      robots: 'noindex, nofollow',
      jsonLd: [],
    };
  }

  if (path === '/call') {
    return {
      lang,
      title: `${t.callTitle} | ${SITE_NAME}`,
      description: t.callLead,
      canonicalPath: pathWithLang(path, lang),
      alternates: bilingualAlternates(path),
      ogType: 'website',
      status: 200,
      jsonLd: [],
    };
  }

  if (path === '/white-label') {
    return {
      lang,
      title: `${t.whiteLabel.seoTitle} | ${SITE_NAME}`,
      description: t.whiteLabel.seoDescription,
      canonicalPath: pathWithLang(path, lang),
      alternates: bilingualAlternates(path),
      ogType: 'website',
      status: 200,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: t.whiteLabel.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: { '@type': 'Answer', text: faq.a },
          })),
        },
      ],
    };
  }

  const legalTitle = LEGAL_PATHS[path]?.(t);
  if (legalTitle) {
    const title = legalTitle;
    return {
      lang,
      title: `${title} | ${SITE_NAME}`,
      description: t.heroP1,
      canonicalPath: pathWithLang(path, lang),
      alternates: bilingualAlternates(path),
      ogType: 'website',
      status: 200,
      jsonLd: [],
    };
  }

  return {
    lang,
    title: `${t.notFoundTitle} | ${SITE_NAME}`,
    description: t.notFoundBody,
    canonicalPath: path,
    alternates: [],
    ogType: 'website',
    status: 404,
    // Une 404 indexée est une page morte dans l'index : on la retire nous-mêmes.
    robots: 'noindex, follow',
    jsonLd: [],
  };
}

/** Une couverture est un chemin du site ou déjà une URL https complète. */
function absoluteImageUrl(image: string, origin: string): string {
  return image.startsWith('https://') ? image : absoluteUrl(image, origin);
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * `</script>` à l'intérieur d'un JSON-LD fermerait la balise et laisserait le
 * reste du JSON s'exécuter comme du HTML. Les articles viennent de nos propres
 * fichiers markdown, mais c'est exactement le genre de garde qu'on regrette de
 * ne pas avoir mise le jour où un titre contient une balise.
 */
function escapeJsonLd(json: string): string {
  return json.replace(/</g, '\\u003c');
}

export function renderHead(head: HeadData, origin: string): string {
  const canonical = absoluteUrl(head.canonicalPath, origin);
  const tags: string[] = [
    `<title>${escapeAttribute(head.title)}</title>`,
    `<meta name="description" content="${escapeAttribute(head.description)}" />`,
    `<meta property="og:site_name" content="${escapeAttribute(SITE_NAME)}" />`,
    `<meta property="og:type" content="${head.ogType}" />`,
    `<meta property="og:title" content="${escapeAttribute(head.title)}" />`,
    `<meta property="og:description" content="${escapeAttribute(head.description)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
  ];

  // Les réseaux (LinkedIn, X, Slack) ignorent une `og:image` relative : sans
  // origine connue, on n'en écrit pas plutôt que d'en écrire une inutilisable.
  if (head.image && (origin || head.image.startsWith('https://'))) {
    const image = escapeAttribute(absoluteImageUrl(head.image, origin));
    tags.push(`<meta property="og:image" content="${image}" />`);
    tags.push(`<meta name="twitter:image" content="${image}" />`);
  }

  if (head.robots) tags.push(`<meta name="robots" content="${escapeAttribute(head.robots)}" />`);

  // Sans origine connue (domaine pas encore branché), on n'écrit ni canonical
  // ni alternate plutôt que d'en écrire de faux. Voir `lib/site.ts`.
  if (origin) {
    tags.push(`<link rel="canonical" href="${escapeAttribute(canonical)}" />`);
    tags.push(`<meta property="og:url" content="${escapeAttribute(canonical)}" />`);
    for (const alternate of head.alternates) {
      tags.push(
        `<link rel="alternate" hreflang="${escapeAttribute(alternate.hrefLang)}" href="${escapeAttribute(
          absoluteUrl(alternate.href, origin),
        )}" />`,
      );
    }
  }

  for (const block of head.jsonLd) {
    tags.push(`<script type="application/ld+json">${escapeJsonLd(JSON.stringify(block))}</script>`);
  }

  return tags.join('\n    ');
}

/**
 * Côté client, seul le titre et la description ont besoin de suivre la
 * navigation : le reste a déjà été servi par le SSR, et c'est cette version-là
 * que les crawlers lisent.
 */
export function useHead(head: HeadData): void {
  useEffect(() => {
    document.title = head.title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', head.description);

    document.documentElement.lang = head.lang;
  }, [head.title, head.description, head.lang]);
}
